/*
  THAANI — Standorte: Filter nach Region + Karte mit Pins.
  Vanilla JS, kein Framework. Leaflet kommt vom CDN und wird erst geladen,
  wenn die Section in Sichtweite kommt.

  Beides ist reine Anreicherung und im CSS zunaechst aus:
  .is-filterable schaltet die Filterleiste frei, .is-mapped die Kartenspalte.
  Ohne JS, oder wenn das CDN nicht antwortet, bleibt die vollstaendige
  Liste stehen, statt einer Leiste und einer leeren Flaeche, die nichts tun.

  Koordinaten stehen als data-lat/data-lng am <li>, gesetzt aus dem
  Block-Feld "Koordinaten" (ein Feld, Format wie aus Google Maps kopiert).

  Zwei Kartenanbieter, umschaltbar in den Section-Einstellungen:

  - swisstopo (Standard): Kacheln vom Bund, kein Schluessel, keine Cookies,
    laedt sofort.
  - Google Maps: braucht einen API-Schluessel und setzt Cookies. Deshalb laedt
    die Karte dort erst auf Klick (Zwei-Klick-Loesung), vorher steht ein
    Platzhalter mit Hinweis. Die Zustimmung merkt sich der Browser fuer die
    laufende Sitzung, damit man nicht auf jeder Seite neu klicken muss.

  Der Rest der Section kennt den Anbieter nicht: beide liefern denselben
  kleinen Satz Methoden (siehe adapterLeaflet / adapterGoogle), alles andere
  arbeitet dagegen.
*/

const LEAFLET_VERSION = '1.9.4';
const LEAFLET_CSS = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}/leaflet.min.css`;
const LEAFLET_JS = `https://cdnjs.cloudflare.com/ajax/libs/leaflet/${LEAFLET_VERSION}/leaflet.min.js`;

/*
  Kartenkacheln von swisstopo (WMTS des Bundes, frei und ohne Schluessel
  nutzbar, Attribution unten rechts). Kein Fremd-Branding, kein API-Schluessel,
  keine nutzungsabhaengigen Kosten.

  Zwei Ebenen, umgeschaltet nach Zoomstufe:

  - Uebersicht (bis Zoom 11): die gewohnte Landeskarte in Grau. Auf Landes-
    und Kantonsebene das schoenste Kartenbild, mit Seen, Relief und Ortsnamen.
  - Nah (ab Zoom 12): swisstlm3d in Grau. Die Landeskarte zeichnet Haeuser ab
    Strassenzoom als dichte schwarze Schraffur, ganze Quartiere kippen dann in
    ein Muster, in dem der Pin untergeht. Die TLM-Ebene bleibt dort ruhig.

  Jenseits des Kartenblatts (grob: Schweiz plus Grenzstreifen) bleibt die
  Flaeche leer, deshalb bindet initMap den Ausschnitt an die Standorte.
*/
const TILE_SCHWELLE = 12;
const TILE_UEBERSICHT = 'ch.swisstopo.pixelkarte-grau';
const TILE_NAH = 'ch.swisstopo.swisstlm3d-karte-grau';
const TILE_ATTRIBUTION = '&copy; <a href="https://www.swisstopo.admin.ch" rel="noopener">swisstopo</a>';

function kachelEbene(L) {
  return new (L.TileLayer.extend({
    getTileUrl(coords) {
      const z = this._getZoomForUrl();
      const nah = z >= TILE_SCHWELLE;
      const ebene = nah ? TILE_NAH : TILE_UEBERSICHT;
      const endung = nah ? 'png' : 'jpeg';
      return (
        'https://wmts.geo.admin.ch/1.0.0/' +
        ebene +
        '/default/current/3857/' +
        z +
        '/' +
        coords.x +
        '/' +
        coords.y +
        '.' +
        endung
      );
    },
  }))('', { attribution: TILE_ATTRIBUTION, maxZoom: 18 });
}

const PIN_SVG = `
  <svg class="thaani-standorte__pin" viewBox="0 0 28 36" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <path fill="currentColor" d="M14 0C6.8 0 1 5.8 1 13c0 9.2 11.4 21.6 11.9 22.1a1.5 1.5 0 0 0 2.2 0C15.6 34.6 27 22.2 27 13c0-7.2-5.8-13-13-13Z"/>
    <circle cx="14" cy="13" r="4.6" fill="#f7f1e8"/>
  </svg>
`;

let leafletPromise = null;

function loadStylesheet(href) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`link[href="${href}"]`)) {
      resolve();
      return;
    }
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.addEventListener('load', () => resolve());
    link.addEventListener('error', reject);
    document.head.appendChild(link);
  });
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', reject);
      if (window.L) resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.addEventListener('load', () => resolve());
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
}

function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (!leafletPromise) {
    leafletPromise = Promise.all([loadStylesheet(LEAFLET_CSS), loadScript(LEAFLET_JS)]).then(
      () => window.L
    );
  }
  return leafletPromise;
}

/*
  Google Maps laedt ueber den offiziellen Bootstrap-Parameter mit Callback.
  Der Schluessel steckt in den Section-Einstellungen und ist ein oeffentlicher
  Browser-Schluessel: er gehoert in der Google-Konsole auf die eigenen Domains
  eingeschraenkt, sonst kann ihn jeder auf seiner Seite verwenden.
*/
let googlePromise = null;

/*
  Wenn am Schluessel etwas nicht stimmt, ruft Google window.gm_authFailure auf.
  Typische Faelle: Schluessel fehlt, ist auf eine andere Domain eingeschraenkt,
  die Maps JavaScript API ist im Projekt nicht aktiviert, oder es haengt kein
  Abrechnungskonto dran. Google legt dann eine graue Flaeche mit Fehlerdialog
  ueber die Karte. Das darf ein Besucher nie sehen, deshalb sammeln wir hier
  alle eingehaengten Sections ein und schicken sie zurueck auf swisstopo.
*/
const KARTEN_IM_EINSATZ = new Set();

window.gm_authFailure = () => {
  KARTEN_IM_EINSATZ.forEach((section) => section.zurueckAufSwisstopo('Google lehnt den Schlüssel ab'));
};

const GOOGLE_TIMEOUT = 10000;

function loadGoogle(schluessel) {
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (!googlePromise) {
    googlePromise = new Promise((resolve, reject) => {
      const name = '__thaaniKarteBereit';
      let erledigt = false;

      /*
        Ohne Zeitgrenze haengt die Kartenspalte leer, wenn das Skript zwar
        laedt, der Callback aber nie kommt (blockiert, offline, Google down).
      */
      const uhr = setTimeout(() => {
        if (erledigt) return;
        erledigt = true;
        reject(new Error('Google Maps antwortet nicht'));
      }, GOOGLE_TIMEOUT);

      window[name] = () => {
        if (erledigt) return;
        erledigt = true;
        clearTimeout(uhr);
        delete window[name];
        resolve(window.google);
      };

      const script = document.createElement('script');
      script.src =
        'https://maps.googleapis.com/maps/api/js?key=' +
        encodeURIComponent(schluessel) +
        '&loading=async&language=de&region=CH&callback=' +
        name;
      script.async = true;
      script.addEventListener('error', () => {
        if (erledigt) return;
        erledigt = true;
        clearTimeout(uhr);
        reject(new Error('Google Maps laedt nicht'));
      });
      document.head.appendChild(script);
    });
    /* Ein gescheiterter Versuch darf den naechsten nicht blockieren */
    googlePromise.catch(() => {
      googlePromise = null;
    });
  }
  return googlePromise;
}

/*
  Kartenstil fuer Google: Beschriftung und Strassen bleiben, alles Bunte geht
  raus. Ohne das steht eine knallige Standardkarte in einer Seite, die sonst
  nur Bone, Linen und Indigo kennt. Punkte von Interesse sind aus, sie lenken
  von unseren eigenen Pins ab.
*/
const GOOGLE_STIL = [
  { elementType: 'geometry', stylers: [{ color: '#efe9df' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#5c5148' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#f7f1e8' }] },
  { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#d6cabb' }] },
  { featureType: 'landscape.natural', elementType: 'geometry', stylers: [{ color: '#e8e2d6' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#dfe3d3' }, { visibility: 'on' }] },
  /* Nur die Flaeche der Parks bleibt, ihre Beschriftung und die Berg-Icons nicht */
  { featureType: 'poi.park', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape.natural', elementType: 'labels', stylers: [{ visibility: 'off' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#f0e2c8' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#cdd7dd' }] },
];

const PIN_RUHIG = '#4b2673';
const PIN_AKTIV = '#a4388d';

function pinBild(farbe) {
  const svg =
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 28 36" width="28" height="36">' +
    '<path fill="' +
    farbe +
    '" d="M14 0C6.8 0 1 5.8 1 13c0 9.2 11.4 21.6 11.9 22.1a1.5 1.5 0 0 0 2.2 0C15.6 34.6 27 22.2 27 13c0-7.2-5.8-13-13-13Z"/>' +
    '<circle cx="14" cy="13" r="4.6" fill="#f7f1e8"/></svg>';
  return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
}

/*
  Beide Anbieter liefern denselben Satz Methoden, damit der Rest der Section
  nicht wissen muss, welche Karte gerade laeuft.
*/
function adapterLeaflet(L, node, section) {
  const karte = L.map(node, {
    scrollWheelZoom: false,
    zoomControl: true,
    attributionControl: true,
    minZoom: 7,
    maxZoom: 18,
    // Auf Touch-Geraeten soll ein Wisch die Seite scrollen, nicht die Karte
    // verschieben; die Zoom-Buttons bleiben. tap: false verhindert, dass
    // Leaflet Klicks auf Marker doppelt ausloest.
    dragging: !L.Browser.mobile,
    tap: false,
  });
  kachelEbene(L).addTo(karte);

  const icon = L.divIcon({
    className: 'thaani-standorte__marker',
    html: PIN_SVG,
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -32],
  });

  return {
    name: 'leaflet',
    karte,
    pinSetzen(item, lat, lng) {
      const pin = L.marker([lat, lng], { icon, title: section.nameOf(item), keyboard: false });
      pin.bindPopup(section.popupOf(item));
      pin.on('click', () => section.focusItem(item, false, true));
      return pin;
    },
    sichtbar(item, an) {
      if (an) item.pin.addTo(karte);
      else karte.removeLayer(item.pin);
    },
    grenzenSetzen(items) {
      if (items.length < 2) return;
      const alle = L.latLngBounds(items.map((item) => item.pin.getLatLng()));
      karte.setMaxBounds(alle.pad(0.6));
    },
    ausschnitt(items) {
      const bounds = L.latLngBounds(items.map((item) => item.pin.getLatLng()));
      karte.fitBounds(bounds, { padding: [48, 48], maxZoom: 13, animate: false });
    },
    anfliegen(item, sanft) {
      const ziel = item.pin.getLatLng();
      const zoom = Math.max(karte.getZoom(), 14);
      if (sanft) karte.flyTo(ziel, zoom, { duration: 0.6 });
      else karte.setView(ziel, zoom);
    },
    infoOeffnen(item) {
      item.pin.openPopup();
    },
    aktivMarkieren(item, an) {
      const el = item.pin.getElement();
      if (el) el.classList.toggle('is-active', an);
    },
    groesseNeu() {
      karte.invalidateSize();
    },
    zerstoeren() {
      karte.remove();
    },
  };
}

function adapterGoogle(google, node, section) {
  const karte = new google.maps.Map(node, {
    center: { lat: 46.8, lng: 8.23 },
    zoom: 8,
    styles: GOOGLE_STIL,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    scrollwheel: false,
    gestureHandling: 'cooperative',
    minZoom: 7,
    maxZoom: 18,
  });
  const info = new google.maps.InfoWindow();
  const ruhig = pinBild(PIN_RUHIG);
  const aktiv = pinBild(PIN_AKTIV);
  const symbol = (bild) => ({
    url: bild,
    scaledSize: new google.maps.Size(28, 36),
    anchor: new google.maps.Point(14, 36),
  });

  return {
    name: 'google',
    karte,
    pinSetzen(item, lat, lng) {
      const pin = new google.maps.Marker({
        position: { lat, lng },
        title: section.nameOf(item),
        icon: symbol(ruhig),
      });
      pin.addListener('click', () => section.focusItem(item, false, true));
      return pin;
    },
    sichtbar(item, an) {
      item.pin.setMap(an ? karte : null);
    },
    grenzenSetzen(items) {
      if (items.length < 2) return;
      const bounds = new google.maps.LatLngBounds();
      items.forEach((item) => bounds.extend(item.pin.getPosition()));
      /* Etwas Luft, damit man um die Standorte herum noch schieben kann */
      const nw = bounds.getNorthEast();
      const sw = bounds.getSouthWest();
      const luftLat = Math.max((nw.lat() - sw.lat()) * 0.6, 0.2);
      const luftLng = Math.max((nw.lng() - sw.lng()) * 0.6, 0.2);
      karte.setOptions({
        restriction: {
          latLngBounds: {
            north: nw.lat() + luftLat,
            east: nw.lng() + luftLng,
            south: sw.lat() - luftLat,
            west: sw.lng() - luftLng,
          },
          strictBounds: false,
        },
      });
    },
    ausschnitt(items) {
      const bounds = new google.maps.LatLngBounds();
      items.forEach((item) => bounds.extend(item.pin.getPosition()));
      karte.fitBounds(bounds, 48);
      /* fitBounds zoomt bei einem einzelnen Pin bis Anschlag, deshalb deckeln */
      google.maps.event.addListenerOnce(karte, 'idle', () => {
        if (karte.getZoom() > 13) karte.setZoom(13);
      });
    },
    anfliegen(item, sanft) {
      const ziel = item.pin.getPosition();
      if (karte.getZoom() < 14) karte.setZoom(14);
      if (sanft) karte.panTo(ziel);
      else karte.setCenter(ziel);
    },
    infoOeffnen(item) {
      info.setContent(section.popupOf(item));
      info.open({ anchor: item.pin, map: karte });
    },
    aktivMarkieren(item, an) {
      item.pin.setIcon(symbol(an ? aktiv : ruhig));
      item.pin.setZIndex(an ? 999 : 1);
    },
    groesseNeu() {
      google.maps.event.trigger(karte, 'resize');
    },
    zerstoeren() {
      info.close();
    },
  };
}

const EINWILLIGUNG = 'thaani-karte-google';

function einwilligungGegeben() {
  try {
    return window.sessionStorage.getItem(EINWILLIGUNG) === 'ja';
  } catch (fehler) {
    return false;
  }
}

function einwilligungMerken() {
  try {
    window.sessionStorage.setItem(EINWILLIGUNG, 'ja');
  } catch (fehler) {
    /* Privater Modus: dann fragt die naechste Seite eben nochmal */
  }
}

class ThaaniStandorte extends HTMLElement {
  connectedCallback() {
    this.chips = Array.from(this.querySelectorAll('.thaani-standorte__chip'));
    this.items = Array.from(this.querySelectorAll('.thaani-standorte__item'));
    this.panel = this.querySelector('.thaani-standorte__panel');
    this.empty = this.querySelector('.thaani-standorte__empty');
    this.mapNode = this.querySelector('.thaani-standorte__map');
    this.consentNode = this.querySelector('.thaani-standorte__consent');
    this.anbieter = this.dataset.karte === 'google' ? 'google' : 'swisstopo';
    this.googleKey = this.dataset.googleKey || '';

    if (this.items.length === 0) return;

    if (this.chips.length > 0) this.classList.add('is-filterable');

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    if (this.dataset.map === 'true' && this.mapNode) this.observeMap();

    /* Im Theme-Editor: ausgewaehlter Block springt auf der Karte an */
    this.onBlockSelect = this.onBlockSelect.bind(this);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  disconnectedCallback() {
    KARTEN_IM_EINSATZ.delete(this);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    if (this.observer) this.observer.disconnect();
    if (this.karte) {
      this.karte.zerstoeren();
      this.karte = null;
    }
  }

  /* ---------------- Filter ---------------- */

  onClick(event) {
    const chip = event.target.closest('.thaani-standorte__chip');
    if (chip && this.contains(chip)) {
      this.select(chip.dataset.region);
      return;
    }

    const zustimmen = event.target.closest('.thaani-standorte__consent-button');
    if (zustimmen && this.contains(zustimmen)) {
      einwilligungMerken();
      this.karteLaden();
      return;
    }

    /* Links im Kartenblatt behalten ihr eigenes Verhalten */
    if (event.target.closest('a')) return;

    if (!this.karte) return;

    const item = event.target.closest('.thaani-standorte__item');
    if (item && this.contains(item) && item.dataset.lat) this.focusItem(item, true);
  }

  select(region) {
    let visible = 0;

    this.chips.forEach((chip) => {
      const active = chip.dataset.region === region;
      chip.classList.toggle('is-active', active);
      chip.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    this.items.forEach((item) => {
      const match = region === 'alle' || item.dataset.region === region;
      item.hidden = !match;
      if (match) visible += 1;
    });

    if (this.empty) this.empty.hidden = visible > 0;

    this.syncMarkers();
  }

  onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const item = this.items.find((entry) => entry.dataset.blockId === event.detail.blockId);
    if (item && item.dataset.lat) this.focusItem(item, false);
  }

  /* ---------------- Karte ---------------- */

  observeMap() {
    const start = () => {
      if (this.observer) this.observer.disconnect();
      this.karteVorbereiten();
    };

    if (!('IntersectionObserver' in window)) {
      start();
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) start();
      },
      { rootMargin: '300px' }
    );
    this.observer.observe(this);
  }

  /*
    Google darf erst nach Zustimmung laden, swisstopo sofort. Ohne Schluessel
    bleibt die Spalte aus, ein Platzhalter ohne Aussicht auf Karte hilft
    niemandem.
  */
  karteVorbereiten() {
    if (this.anbieter === 'google') {
      if (!this.googleKey) return;
      if (!einwilligungGegeben()) {
        this.classList.add('is-mapped');
        if (this.consentNode) {
          this.consentNode.hidden = false;
          this.consentNode.parentElement.classList.add('wartet-auf-zustimmung');
        }
        return;
      }
    }
    this.karteLaden();
  }

  karteLaden() {
    if (this.karte) return;
    if (this.consentNode) {
      this.consentNode.hidden = true;
      this.consentNode.parentElement.classList.remove('wartet-auf-zustimmung');
    }

    const fertig = (bauen) => {
      this.initMap(bauen);
    };

    if (this.anbieter === 'google') {
      KARTEN_IM_EINSATZ.add(this);
      loadGoogle(this.googleKey)
        .then((google) => fertig((node) => adapterGoogle(google, node, this)))
        .catch(() => this.zurueckAufSwisstopo('Google Maps laedt nicht'));
    } else {
      loadLeaflet()
        .then((L) => fertig((node) => adapterLeaflet(L, node, this)))
        .catch(() => this.karteAus());
    }
  }

  /*
    Google klappt nicht: statt einer leeren oder fehlerbedeckten Flaeche
    kommt die swisstopo-Karte. Der Besucher merkt nichts, im Log steht der
    Grund fuer uns.
  */
  zurueckAufSwisstopo(grund) {
    if (this.anbieter !== 'google') return;
    KARTEN_IM_EINSATZ.delete(this);
    console.warn('[THAANI Standorte] ' + grund + ', zeige swisstopo.');

    if (this.karte) {
      this.karte.zerstoeren();
      this.karte = null;
    }
    this.mapNode.innerHTML = '';
    this.classList.remove('is-live');
    this.anbieter = 'swisstopo';
    this.karteLaden();
  }

  /* Anbieter nicht erreichbar: Liste bleibt, Kartenspalte verschwindet */
  karteAus() {
    this.classList.remove('is-mapped', 'is-live');
    if (this.consentNode) {
      this.consentNode.hidden = true;
      this.consentNode.parentElement.classList.remove('wartet-auf-zustimmung');
    }
  }

  initMap(bauen) {
    if (this.karte) return;

    this.karte = bauen(this.mapNode);

    this.mitPin = [];
    this.items.forEach((item) => {
      const lat = parseFloat(item.dataset.lat);
      const lng = parseFloat(item.dataset.lng);
      if (Number.isNaN(lat) || Number.isNaN(lng)) return;
      item.pin = this.karte.pinSetzen(item, lat, lng);
      this.mitPin.push(item);
    });

    if (this.mitPin.length === 0) {
      this.karte.zerstoeren();
      this.karte = null;
      this.karteAus();
      return;
    }

    this.mitPin.forEach((item) => this.karte.sichtbar(item, true));
    this.karte.grenzenSetzen(this.mitPin);

    this.classList.add('is-mapped', 'is-live');
    this.syncMarkers();

    /* Nach dem Einblenden der Spalte kennt die Karte ihre Groesse noch nicht */
    requestAnimationFrame(() => this.karte && this.karte.groesseNeu());
  }

  /* Sichtbare Standorte auf die Karte legen und den Ausschnitt darauf ziehen */
  syncMarkers() {
    if (!this.karte) return;

    const sichtbar = [];
    this.mitPin.forEach((item) => {
      const an = !item.hidden;
      this.karte.sichtbar(item, an);
      if (an) sichtbar.push(item);
    });

    if (sichtbar.length === 0) return;
    this.karte.ausschnitt(sichtbar);
  }

  focusItem(item, scrollMapIntoView, fromMarker) {
    this.items.forEach((entry) => {
      const active = entry === item;
      entry.classList.toggle('is-active', active);
      if (entry.pin && this.karte) this.karte.aktivMarkieren(entry, active);
    });

    if (!item.pin || !this.karte) return;

    const motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (fromMarker) {
      this.scrollPanelTo(item);
    } else {
      this.karte.anfliegen(item, !motion);
      if (scrollMapIntoView && window.innerWidth < 990) {
        this.mapNode.scrollIntoView({ behavior: motion ? 'auto' : 'smooth', block: 'center' });
      }
    }

    this.karte.infoOeffnen(item);
  }

  /* Die Liste scrollt nur in sich selbst, die Seite bleibt stehen */
  scrollPanelTo(item) {
    if (!this.panel) return;
    const scrollable = this.panel.scrollHeight > this.panel.clientHeight + 1;
    if (!scrollable) return;

    const offset =
      item.getBoundingClientRect().top -
      this.panel.getBoundingClientRect().top +
      this.panel.scrollTop;

    this.panel.scrollTo({
      top: offset - 16,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  /* Der Button traegt neben dem Namen noch einen Hinweis fuer Screenreader */
  nameOf(item) {
    const name = item.querySelector('.thaani-standorte__focus');
    if (!name) return '';
    const copy = name.cloneNode(true);
    copy.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
    return copy.textContent.trim();
  }

  popupOf(item) {
    const wrapper = document.createElement('div');
    wrapper.className = 'thaani-standorte__popup';

    const name = document.createElement('span');
    name.className = 'thaani-standorte__popup-name';
    name.textContent = this.nameOf(item);
    wrapper.appendChild(name);

    const address = item.querySelector('.thaani-standorte__address');
    if (address) {
      const line = document.createElement('span');
      line.className = 'thaani-standorte__popup-address';
      line.innerHTML = address.innerHTML;
      wrapper.appendChild(line);
    }

    const link = item.querySelector('.thaani-standorte__link');
    if (link) {
      const copy = document.createElement('a');
      copy.className = 'thaani-standorte__popup-link';
      copy.href = link.href;
      copy.target = '_blank';
      copy.rel = 'noopener';
      // Der Screenreader-Hinweis "(öffnet in neuem Tab)" gehoert nicht in den
      // sichtbaren Popup-Text: Klon bereinigen, dann den Text uebernehmen.
      const linkCopy = link.cloneNode(true);
      linkCopy.querySelectorAll('.visually-hidden').forEach((node) => node.remove());
      copy.textContent = linkCopy.textContent.replace('→', '').trim();
      wrapper.appendChild(copy);
    }

    return wrapper;
  }
}

if (!customElements.get('thaani-standorte')) {
  customElements.define('thaani-standorte', ThaaniStandorte);
}
